import { fromJS } from 'immutable';
import ACTIONS from '../app.constants';
import {
    AccountingTransactionDisplayList,
    AccountingTransactionList,
    Form,
    Appointment,
    Comment,
    LicenseList,
    Inspection,
    CommentList,
    InspectionList,
    File,
} from '../modules/application/types';

import {
    Person,
    PersonReducerActionType,
    PersonReducer,
    Contact,
    PersonData,
    PersonAccountingReducerAction,
} from '../modules/person/types';

export default function personReducer(
    state = fromJS({
        personDetail: {},
        personContacts: {},
        personEditForm: {},
        personLicenses: {},
        personAccounting: {},
        personComment: {},
        linkedLicenses: {},
        personHistory: {},
    }),
    action: PersonReducerActionType,
): PersonReducer {
    switch (action.type) {
        case ACTIONS.GET_PERSON_DETAIL.PENDING: {
            return state.setIn(['personDetail', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_PERSON_DETAIL.SUCCESS: {
            const personData = action.data as PersonData;
            return state
                .setIn(['personDetail', 'loading'], false)
                .setIn(['personDetail', 'data'], personData.person)
                .setIn(['personType', 'data'], personData.personType)
                .setIn(['personEditForm', 'data'], personData.editForm)
                .setIn(['personDetail', 'contactsCount'], personData.person.contacts?.length ?? 0);
        }
        case ACTIONS.GET_PERSON_DETAIL.ERROR: {
            return state.setIn(['personDetail', 'loading'], false);
        }
        case ACTIONS.GET_PERSON_CONTACTS.PENDING: {
            return state.setIn(['personContacts', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_PERSON_CONTACTS.SUCCESS: {
            let loadedList = state.getIn(['personContacts', 'data']) ?? [];
            const personContacts = action.data;

            const loadMore = personContacts.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(personContacts);
            } else {
                loadedList = personContacts;
            }
            return state
                .setIn(['personContacts', 'loading'], false)
                .setIn(['personContacts', 'loadMore'], loadMore)
                .setIn(['personContacts', 'data'], loadedList)
                .setIn(['personDetail', 'data', 'contacts'], loadedList);
        }
        case ACTIONS.GET_PERSON_CONTACTS.ERROR: {
            return state.setIn(['personContacts', 'loading'], false);
        }

        case ACTIONS.GET_CONTACT_TYPES.PENDING: {
            return state.setIn(['contactTypes', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_CONTACT_TYPES.SUCCESS: {
            const { data } = action;
            return state.setIn(['contactTypes', 'loading'], false).setIn(['contactTypes', 'data'], data);
        }
        case ACTIONS.GET_CONTACT_TYPES.ERROR: {
            return state.setIn(['contactTypes', 'loading'], false);
        }
        case ACTIONS.UPDATE_PERSON.PENDING: {
            return state.setIn(['personDetail', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.UPDATE_PERSON.SUCCESS: {
            const personData: Person = state.getIn(['personDetail', 'data']);
            return state.setIn(['personDetail', 'loading'], false).setIn(['personDetail', 'data'], personData);
        }
        case ACTIONS.UPDATE_PERSON.ERROR: {
            return state.setIn(['personDetail', 'loading'], false).setIn(['personDetail', 'error'], action.error);
        }
        case ACTIONS.GET_PERSON_LICENSES.PENDING: {
            return state.setIn(['personLicenses', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_PERSON_LICENSES.SUCCESS: {
            let loadedList = state.getIn(['personLicenses', 'data']) ?? [];
            const personLicenses: LicenseList = action.data as LicenseList;

            const loadMore = personLicenses.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(personLicenses);
            } else {
                loadedList = personLicenses;
            }
            return state
                .setIn(['personLicenses', 'loading'], false)
                .setIn(['personLicenses', 'loadMore'], loadMore)
                .setIn(['personLicenses', 'data'], loadedList)
                .setIn(['personLicenses', 'licenseCount'], action.licenseCount);
        }
        case ACTIONS.GET_PERSON_LICENSES.ERROR: {
            return state.setIn(['personLicenses', 'loading'], false);
        }

        case ACTIONS.GET_PERSON_ACCOUNTING.PENDING: {
            return state.setIn(['personAccounting', 'loading'], action.status === 'LOADING');
        }
        case ACTIONS.GET_PERSON_ACCOUNTING.SUCCESS: {
            let loadedList = state.getIn(['personAccounting', 'data']) ?? [];

            const transactionList: AccountingTransactionList = (action as PersonAccountingReducerAction).data;

            const loadMore = transactionList.length >= (action.paginationData?.limit ?? Infinity);

            const displayList: AccountingTransactionDisplayList = transactionList.map((transaction) => ({
                date: transaction.journalEntry.date,
                description: transaction.description,
                chargeAmount: transaction.type === 'debit' ? transaction.amount : undefined,
                paymentAmount: transaction.type === 'credit' ? transaction.amount : undefined,
                balance: transaction.balance,
            }));

            if (loadedList && action.paginationData?.skip) {
                loadedList = loadedList.concat(displayList);
            } else {
                loadedList = displayList;
            }

            return state
                .setIn(['personAccounting', 'loading'], false)
                .setIn(['personAccounting', 'loadMore'], loadMore)
                .setIn(['personAccounting', 'data'], loadedList)
                .setIn(['licenseAccounting', 'error'], false);
        }
        case ACTIONS.GET_PERSON_ACCOUNTING.ERROR: {
            return state
                .setIn(['personAccounting', 'loading'], false)
                .setIn(['licenseAccounting', 'data'], [])
                .setIn(['licenseAccounting', 'error'], true);
        }

        case ACTIONS.GET_APPOINTMENT_LIST.PENDING: {
            return state.setIn(['appointmentList', 'loading'], action.status === 'LOADING' ? true : false);
        }

        case ACTIONS.GET_APPOINTMENT_LIST.SUCCESS: {
            let loadedList = state.getIn(['appointmentList', 'data']) ?? [];
            const appointmentList = action.data;

            const loadMore = appointmentList.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(appointmentList);
            } else {
                loadedList = appointmentList;
            }
            return state
                .setIn(['appointmentList', 'loading'], false)
                .setIn(['appointmentList', 'loadMore'], loadMore)
                .setIn(['appointmentList', 'data'], loadedList)
                .setIn(['appointmentList', 'appointmentCount'], action.appointmentCount);
        }

        case ACTIONS.GET_APPOINTMENT_LIST.ERROR: {
            return state.setIn(['appointmentList', 'loading'], false);
        }

        case ACTIONS.GET_APPOINTMENT_INSPECTION_LIST.PENDING: {
            return state.setIn(['appointmentInspection', 'loading'], action.status === 'LOADING' ? true : false);
        }

        case ACTIONS.GET_APPOINTMENT_INSPECTION_LIST.SUCCESS: {
            let loadedList = state.getIn(['appointmentInspection', 'data']) ?? [];
            const appointmentInspections: InspectionList = action.data as InspectionList;

            const loadMore = appointmentInspections.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(appointmentInspections);
            } else {
                loadedList = appointmentInspections;
            }
            return state
                .setIn(['appointmentInspection', 'loading'], false)
                .setIn(['appointmentInspection', 'loadMore'], loadMore)
                .setIn(['appointmentInspection', 'data'], loadedList)
                .setIn(['appointmentInspection', 'inspectionCount'], action.inspectionCount);
        }
        case ACTIONS.GET_APPOINTMENT_INSPECTION_LIST.ERROR: {
            return state.setIn(['appointmentInspection', 'loading'], false);
        }

        case ACTIONS.ADD_APPOINTMENT_INSPECTION.PENDING: {
            return state.setIn(['appointmentInspection', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_APPOINTMENT_INSPECTION.SUCCESS: {
            const data = action.data;
            const inspection = state.getIn(['appointmentInspection', 'data']);
            const newInspectionData = inspection.concat(data);
            const appointmentInspectionCount = state.getIn(['appointmentInspection', 'inspectionCount']);

            return state
                .setIn(['appointmentInspection', 'loading'], false)
                .setIn(['appointmentInspection', 'data'], newInspectionData)
                .setIn(['appointmentInspection', 'inspectionCount'], appointmentInspectionCount + 1);
        }

        case ACTIONS.ADD_APPOINTMENT_INSPECTION.ERROR: {
            return state.setIn(['appointmentInspection', 'loading'], false);
        }

        case ACTIONS.GET_APPOINTMENT_TYPE_LIST.PENDING: {
            return state.setIn(['appointmentTypeList', 'loading'], action.status === 'LOADING' ? true : false);
        }

        case ACTIONS.GET_APPOINTMENT_TYPE_LIST.SUCCESS: {
            return state
                .setIn(['appointmentTypeList', 'loading'], false)
                .setIn(['appointmentTypeList', 'data'], action.data);
        }

        case ACTIONS.GET_APPOINTMENT_TYPE_LIST.ERROR: {
            return state.setIn(['appointmentTypeList', 'loading'], false);
        }

        case ACTIONS.ADD_PERSON_CONTACT.PENDING: {
            return state.setIn(['personContacts', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_PERSON_CONTACT.SUCCESS: {
            const { data }: any = action;
            const contacts: Contact[] = state.getIn(['personContacts', 'data']) ?? [];
            const contactsCount: number = state.getIn(['personDetail', 'contactsCount']);
            const newContactData = contacts.concat(data);
            return state
                .setIn(['personContacts', 'loading'], false)
                .setIn(['personContacts', 'data'], newContactData)
                .setIn(['personDetail', 'data', 'contacts'], newContactData)
                .setIn(['personDetail', 'contactsCount'], contactsCount + 1);
        }
        case ACTIONS.ADD_PERSON_CONTACT.ERROR: {
            return state.setIn(['personContacts', 'loading'], false).setIn(['personContacts', 'error'], action.error);
        }
        case ACTIONS.UPDATE_PERSON_CONTACT.PENDING: {
            return state.setIn(['personContacts', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.UPDATE_PERSON_CONTACT.SUCCESS: {
            const { data }: any = action;
            const personContacts = fromJS(state.getIn(['personContacts', 'data']));
            const updatedPersonContactList = personContacts.update(
                personContacts.findIndex((item: any) => {
                    return item.get('id') === data.id;
                }),
                (item: any) => {
                    item = fromJS(data);
                    return item;
                },
            );
            return state
                .setIn(['personContacts', 'loading'], false)
                .setIn(['personContacts', 'data'], updatedPersonContactList.toJS());
        }
        case ACTIONS.UPDATE_PERSON_CONTACT.ERROR: {
            return state.setIn(['personContacts', 'loading'], false).setIn(['personContacts', 'error'], action.error);
        }
        case ACTIONS.ADD_PERSON.PENDING: {
            return state.setIn(['personDetail', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_PERSON.SUCCESS: {
            return state.setIn(['personDetail', 'loading'], false);
        }
        case ACTIONS.ADD_PERSON.ERROR: {
            return state.setIn(['personDetail', 'loading'], false).setIn(['personDetail', 'error'], action.error);
        }

        case ACTIONS.GET_LICENSE_LIST_APPOINTMENT.PENDING: {
            return state.setIn(
                ['applicationListForAppointment', 'loading'],
                action.status === 'LOADING' ? true : false,
            );
        }
        case ACTIONS.GET_LICENSE_LIST_APPOINTMENT.SUCCESS: {
            return state
                .setIn(['applicationListForAppointment', 'loading'], false)
                .setIn(['applicationListForAppointment', 'data'], action.data);
        }
        case ACTIONS.GET_LICENSE_LIST_APPOINTMENT.ERROR: {
            return state.setIn(['applicationListForAppointment', 'loading'], false);
        }

        case ACTIONS.ADD_APPOINTMENT.PENDING: {
            return state.setIn(['appointmentList', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_APPOINTMENT.SUCCESS: {
            const data = action.data;
            const appointments: any[] = state.getIn(['appointmentList', 'data']);
            const appointmentCount: number = state.getIn(['appointmentList', 'appointmentCount']);
            appointments.push(data);

            return state
                .setIn(['appointmentList', 'loading'], false)
                .setIn(['appointmentList', 'data'], appointments)
                .setIn(['appointmentList', 'appointmentCount'], appointmentCount + 1);
        }
        case ACTIONS.ADD_APPOINTMENT.ERROR: {
            return state.setIn(['appointmentList', 'loading'], false).setIn(['appointmentList', 'error'], action.error);
        }

        case ACTIONS.GET_APPOINTMENT_DETAIL_BY_ID.PENDING: {
            return state.setIn(['appointmentDetail', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_APPOINTMENT_DETAIL_BY_ID.SUCCESS: {
            const appointmentData = action.data as { appointment: Appointment; editForm?: Form };
            return state
                .setIn(['appointmentDetail', 'loading'], false)
                .setIn(['appointmentDetail', 'data'], appointmentData.appointment)
                .setIn(['appointmentEditForm', 'data'], appointmentData.editForm);
        }
        case ACTIONS.GET_APPOINTMENT_DETAIL_BY_ID.ERROR: {
            return state.setIn(['appointmentDetail', 'loading'], false);
        }

        case ACTIONS.UPDATE_APPOINTMENT.PENDING: {
            return state.setIn(['appointmentDetail', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.UPDATE_APPOINTMENT.SUCCESS: {
            return state
                .setIn(['appointmentDetail', 'loading'], false)
                .setIn(['appointmentDetail', 'data'], action.data);
        }
        case ACTIONS.UPDATE_APPOINTMENT.ERROR: {
            return state
                .setIn(['appointmentDetail', 'loading'], false)
                .setIn(['appointmentDetail', 'error'], action.error);
        }
        case ACTIONS.DELETE_APPOINTMENT_RECORD.PENDING: {
            return state.setIn(['appointmentList', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DELETE_APPOINTMENT_RECORD.SUCCESS: {
            const appointmentId = action.payload.appointmentId;

            const appointmentList = state.getIn(['appointmentList', 'data']);
            const filteredAppointments = appointmentList.filter(
                (appointment: Appointment) => appointment.id !== appointmentId,
            );
            const appointmentCount: number = state.getIn(['appointmentList', 'appointmentCount']);
            return state
                .setIn(['appointmentList', 'loading'], false)
                .setIn(['appointmentList', 'data'], filteredAppointments)
                .setIn(['appointmentList', 'appointmentCount'], appointmentCount - 1);
        }
        case ACTIONS.DELETE_APPOINTMENT_RECORD.ERROR: {
            return state.setIn(['appointmentList', 'loading'], false).setIn(['appointmentList', 'error'], action.error);
        }
        case ACTIONS.DELETE_APPOINTMENT_INSPECTION.PENDING: {
            return state.setIn(['appointmentInspection', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DELETE_APPOINTMENT_INSPECTION.SUCCESS: {
            const inspectionId = action.payload.inspectionId;

            const inspectionList = state.getIn(['appointmentInspection', 'data']);
            const filteredInspections = inspectionList.filter(
                (inspection: Inspection) => inspection.id !== inspectionId,
            );
            return state
                .setIn(['appointmentInspection', 'loading'], false)
                .setIn(['appointmentInspection', 'data'], filteredInspections);
        }
        case ACTIONS.DELETE_APPOINTMENT_INSPECTION.ERROR: {
            return state
                .setIn(['appointmentInspection', 'loading'], false)
                .setIn(['appointmentInspection', 'error'], action.error);
        }
        case ACTIONS.DELETE_CONTACT_RECORD.PENDING: {
            return state.setIn(['personContacts', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DELETE_CONTACT_RECORD.SUCCESS: {
            const contactId = action.payload.contactId;

            const contactList = state.getIn(['personContacts', 'data']);
            const filteredContacts = contactList.filter((contact: Contact) => contact.id !== contactId);
            const contactsCount: number = state.getIn(['personDetail', 'contactsCount']);
            return state
                .setIn(['personContacts', 'loading'], false)
                .setIn(['personContacts', 'data'], filteredContacts)
                .setIn(['personDetail', 'data', 'contacts'], filteredContacts)
                .setIn(['personDetail', 'contactsCount'], contactsCount - 1);
        }
        case ACTIONS.DELETE_CONTACT_RECORD.ERROR: {
            return state.setIn(['personContacts', 'loading'], false).setIn(['personContacts', 'error'], action.error);
        }

        case ACTIONS.GET_PERSON_COMMENT_LIST.PENDING: {
            return state.setIn(['personComment', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_PERSON_COMMENT_LIST.SUCCESS: {
            let loadedList = state.getIn(['personComment', 'data']) ?? [];
            const commentList: CommentList = action.data as CommentList;
            const loadMore = commentList.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(commentList);
            } else {
                loadedList = commentList;
            }
            return state.setIn(['personComment', 'loadMore'], loadMore).setIn(['personComment', 'data'], loadedList);
        }
        case ACTIONS.GET_PERSON_COMMENT_LIST.ERROR: {
            return state.setIn(['personComment', 'loading'], false);
        }

        case ACTIONS.ADD_PERSON_COMMENT.PENDING: {
            return state.setIn(['personComment', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_PERSON_COMMENT.SUCCESS: {
            const data = action.data;
            const personComment: Comment[] = state.getIn(['personComment', 'data']);
            const newCommentData = personComment.concat(data);
            return state.setIn(['personComment', 'loading'], false).setIn(['personComment', 'data'], newCommentData);
        }
        case ACTIONS.ADD_PERSON_COMMENT.ERROR: {
            return state.setIn(['personComment', 'loading'], false).setIn(['personComment', 'error'], action.error);
        }

        case ACTIONS.ADD_APPOINTMENT_COMMENT.PENDING: {
            return state.setIn(['appointmentComment', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_APPOINTMENT_COMMENT.SUCCESS: {
            const data = action.data;
            const appointmentComment: any[] = state.getIn(['appointmentComment', 'data']);
            const newCommentData = appointmentComment.concat(data);
            return state
                .setIn(['appointmentComment', 'loading'], false)
                .setIn(['appointmentComment', 'data'], newCommentData);
        }
        case ACTIONS.ADD_APPOINTMENT_COMMENT.ERROR: {
            return state
                .setIn(['appointmentComment', 'loading'], false)
                .setIn(['appointmentComment', 'error'], action.error);
        }

        case ACTIONS.APPOINTMENT_COMMENT_LIST.PENDING: {
            return state.setIn(['appointmentComment', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.APPOINTMENT_COMMENT_LIST.SUCCESS: {
            let loadedList = state.getIn(['appointmentComment', 'data']) ?? [];
            const commentList: CommentList = action.data as CommentList;
            const loadMore = commentList.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(commentList);
            } else {
                loadedList = commentList;
            }
            return state
                .setIn(['appointmentComment', 'loadMore'], loadMore)
                .setIn(['appointmentComment', 'data'], loadedList);
        }

        case ACTIONS.APPOINTMENT_COMMENT_LIST.ERROR: {
            return state.setIn(['appointmentComment', 'loading'], false);
        }

        case ACTIONS.GET_PERSON_TYPES.PENDING: {
            return state.setIn(['personTypes', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_PERSON_TYPES.SUCCESS: {
            return state.setIn(['personTypes', 'loading'], false).setIn(['personTypes', 'data'], action.data);
        }
        case ACTIONS.GET_PERSON_TYPES.ERROR: {
            return state.setIn(['personTypes', 'loading'], false);
        }

        case ACTIONS.GET_PERSON_TYPE_FORM.PENDING: {
            return state.setIn(['personTypeForm', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_PERSON_TYPE_FORM.SUCCESS: {
            return state.setIn(['personTypeForm', 'loading'], false).setIn(['personTypeForm', 'data'], action.data);
        }
        case ACTIONS.GET_PERSON_TYPE_FORM.ERROR: {
            return state.setIn(['personTypeForm', 'loading'], false);
        }

        case ACTIONS.GET_PERSON_FILE_LIST.PENDING: {
            return state.setIn(['personFiles', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_PERSON_FILE_LIST.SUCCESS: {
            let loadedList = state.getIn(['personFiles', 'data']) ?? [];
            const FilesList = action.data;
            const loadMore = FilesList.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(FilesList);
            } else {
                loadedList = FilesList;
            }
            return state
                .setIn(['personFiles', 'loading'], false)
                .setIn(['personFiles', 'loadMore'], loadMore)
                .setIn(['personFiles', 'data'], loadedList);
        }
        case ACTIONS.GET_PERSON_FILE_LIST.ERROR: {
            return state.setIn(['personFiles', 'loading'], false);
        }

        case ACTIONS.DELETE_PERSON_FILE_DOCUMENT.PENDING: {
            return state.setIn(['personFiles', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DELETE_PERSON_FILE_DOCUMENT.SUCCESS: {
            const fileId = decodeURIComponent(action.payload.fileId);
            const files = state.getIn(['personFiles', 'data']);
            const filteredFiles = files.filter((file: File) => file.id !== fileId);
            return state.setIn(['personFiles', 'loading'], false).setIn(['personFiles', 'data'], filteredFiles);
        }
        case ACTIONS.DELETE_PERSON_FILE_DOCUMENT.ERROR: {
            return state.setIn(['personFiles', 'loading'], false).setIn(['personFiles', 'error'], action.error);
        }

        case ACTIONS.DOWNLOAD_PERSON_FILE_DOCUMENT.PENDING: {
            return state.setIn(['personFiles', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DOWNLOAD_PERSON_FILE_DOCUMENT.SUCCESS: {
            return state.setIn(['personFiles', 'loading'], false);
        }
        case ACTIONS.DOWNLOAD_PERSON_FILE_DOCUMENT.ERROR: {
            return state.setIn(['personFiles', 'loading'], false);
        }

        case ACTIONS.GET_ADD_CONTACT_FORM.PENDING: {
            return state.setIn(['addPersonForm', 'loading'], true);
        }
        case ACTIONS.GET_ADD_CONTACT_FORM.SUCCESS: {
            return state.setIn(['addPersonForm', 'loading'], false).setIn(['addPersonForm', 'data'], action.data);
        }
        case ACTIONS.GET_ADD_CONTACT_FORM.ERROR: {
            return state.setIn(['addPersonForm', 'loading'], false);
        }

        case ACTIONS.SET_APPOINTMENT_PERSONS: {
            return state.setIn(['appointmentPersons', 'data'], action.data);
        }

        case ACTIONS.GET_LICENSEE_HISTORY.PENDING: {
            return state.setIn(['personHistory', 'loading'], true).setIn(['personHistory', 'error'], null);
        }
        case ACTIONS.GET_LICENSEE_HISTORY.SUCCESS: {
            return state
                .setIn(['personHistory', 'loading'], false)
                .setIn(['personHistory', 'data'], action.data)
                .setIn(['personHistory', 'error'], null);
        }
        case ACTIONS.GET_LICENSEE_HISTORY.ERROR: {
            return state.setIn(['personHistory', 'loading'], false).setIn(['personHistory', 'error'], action.data);
        }

        default: {
            return state;
        }
    }
}
